import { useEffect, useMemo, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { authorBooks } from './data'
import { getSortedBooks } from './utils'
import { chunkBooksIntoShelves } from './utils'
export function useAuthorBookshelf() {
  const navigate = useNavigate()
  const { bookType } = useParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [selectedGenre, setSelectedGenre] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [viewMode, setViewMode] = useState('spine')
  const [sortOption, setSortOption] = useState('seriesOrder')
  const [darkMode, setDarkMode] = useState(false)
  const [hoveredBookId, setHoveredBookId] = useState<string | null>(null)
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0,
  })
  // New state for organization mode
  const [organizationMode, setOrganizationMode] = useState('all')
  // State for books per shelf - this could be calculated based on screen size
  const [booksPerShelf, setBooksPerShelf] = useState(12)
  // Reference to track container width
  const shelfContainerRef = useRef<HTMLDivElement>(null)
  // State for selected co-author filter
  const [selectedCoAuthor, setSelectedCoAuthor] = useState('all')

  // Check system preference for dark mode and calculate shelf capacity
  useEffect(() => {
    // Get dark mode preference from localStorage
    const savedDarkMode = localStorage.getItem('darkMode') === 'true'
    setDarkMode(savedDarkMode)
    // Track mouse position for hover card
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY,
      })
    }
    // Calculate how many books can fit on a shelf based on container width
    const calculateShelfCapacity = () => {
      if (shelfContainerRef.current) {
        const containerWidth = shelfContainerRef.current.offsetWidth
        // Assuming each book spine is about 60px wide with margins
        const estimatedBooksPerShelf = Math.floor(containerWidth / 60)
        setBooksPerShelf(Math.max(estimatedBooksPerShelf, 5)) // Minimum 5 books per shelf
      }
    }
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('resize', calculateShelfCapacity)
    // Initial calculation
    calculateShelfCapacity()
    // Set organization mode based on URL parameter
    if (bookType) {
      setOrganizationMode('byType')
      setSelectedType(bookType)
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('resize', calculateShelfCapacity)
    }
  }, [bookType])

  // Get all unique genres
  const genres = useMemo(() => {
    return [...new Set(authorBooks.map((book) => book.genre))]
  }, [])

  // Get all unique types
  const types = useMemo(() => {
    return [...new Set(authorBooks.map((book) => book.type))]
  }, [])

  // Filter books based on search, genre, and type
  const filteredBooks = useMemo(() => {
    return authorBooks.filter((book) => {
      const matchesSearch =
        book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (book.series &&
          book.series.toLowerCase().includes(searchTerm.toLowerCase()))
      const matchesGenre = selectedGenre === '' || book.genre === selectedGenre
      const matchesType = selectedType === '' || book.type === selectedType
      return matchesSearch && matchesGenre && matchesType
    })
  }, [searchTerm, selectedGenre, selectedType])

  // Sort books using the helper function
  const sortedBooks = useMemo(
    () => getSortedBooks(filteredBooks),
    [filteredBooks],
  )

  // Split books by type for organized view
  const booksByType = useMemo(() => {
    return {
      series: sortedBooks.filter((book) => book.type === 'series'),
      standalone: sortedBooks.filter((book) => book.type === 'standalone'),
      anthology: sortedBooks.filter((book) => book.type === 'anthology'),
      cowrite: sortedBooks.filter((book) => book.type === 'cowrite'),
      specialEditions: sortedBooks.filter((book) => book.edition === 'special'),
    }
  }, [sortedBooks])

  // Create chunked shelves for the "all" view
  const bookShelves = useMemo(() => {
    return chunkBooksIntoShelves(sortedBooks, booksPerShelf)
  }, [sortedBooks, booksPerShelf])

  // Get series data - all unique series from books
  const seriesData = useMemo(() => {
    const seriesList: Record<string, any[]> = {}
    // Group books by series
    authorBooks
      .filter((book) => book.type === 'series')
      .forEach((book) => {
        if (book.series) {
          if (!seriesList[book.series]) {
            seriesList[book.series] = []
          }
          seriesList[book.series].push(book)
        }
      })
    // Sort books within each series by seriesNumber
    Object.values(seriesList).forEach((books) => {
      books.sort((a, b) => (a.seriesNumber || 0) - (b.seriesNumber || 0))
    })
    return seriesList
  }, [])

  // Get co-author data - all unique co-authors from books
  const coAuthorData = useMemo(() => {
    const coAuthorList: Record<string, any[]> = {}
    // Group books by co-author
    authorBooks
      .filter((book) => book.type === 'cowrite')
      .forEach((book) => {
        if (book.coAuthor) {
          if (!coAuthorList[book.coAuthor]) {
            coAuthorList[book.coAuthor] = []
          }
          coAuthorList[book.coAuthor].push(book)
        }
      })
    // Sort books within each co-author group by release date
    Object.values(coAuthorList).forEach((books) => {
      books.sort(
        (a, b) =>
          new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime(),
      )
    })
    return coAuthorList
  }, [])

  // Get unique co-authors for tabs
  const coAuthors = useMemo(() => {
    return ['all', ...Object.keys(coAuthorData).sort()]
  }, [coAuthorData])

  // Filter co-written books by selected co-author
  const filteredCoWriteBooks = useMemo(() => {
    if (selectedCoAuthor === 'all') {
      return authorBooks.filter((book) => book.type === 'cowrite')
    }
    return authorBooks.filter(
      (book) => book.type === 'cowrite' && book.coAuthor === selectedCoAuthor,
    )
  }, [selectedCoAuthor])

  // Filter special edition books
  const specialEditionBooks = useMemo(() => {
    return authorBooks.filter((book) => book.edition === 'special')
  }, [])

  // Handle navigation to book type page
  const navigateToBookType = (type: string) => {
    navigate(`/authors/bookshelf/${type}`)
  }
  // Navigate to book edit/management page (vendor view)
  const navigateToBookEdit = (bookId: string) => {
    navigate(`/book/${bookId}/edit`)
  }
  return {
    bookType,
    searchTerm,
    setSearchTerm,
    showFilters,
    setShowFilters,
    selectedGenre,
    setSelectedGenre,
    selectedType,
    setSelectedType,
    viewMode,
    setViewMode,
    sortOption,
    setSortOption,
    darkMode,
    hoveredBookId,
    setHoveredBookId,
    mousePosition,
    organizationMode,
    setOrganizationMode,
    booksPerShelf,
    shelfContainerRef,
    selectedCoAuthor,
    setSelectedCoAuthor,
    genres,
    types,
    sortedBooks,
    booksByType,
    bookShelves,
    seriesData,
    coAuthorData,
    coAuthors,
    filteredCoWriteBooks,
    specialEditionBooks,
    navigateToBookType,
    navigateToBookEdit,
    navigate,
  }
}
